import type { PaginationProps } from 'enums/pagination';

import LoggingService from 'components/core/logging/LoggingService';
import { Pagination } from 'enums/pagination';
import { acceptableEmptyParams } from 'utils/filterUtils';
import { graphBaseUrlStorage } from 'utils/graphUrl';

import type { FacetSearchInput, PageInfo, SortInput } from 'store/client/graphql';

export { APIType, getGraphURL } from 'utils/graphUrl';

export const calculatePreviousPaginationParams = (
  startCursor: number,
  pageSize = Pagination.LIST_LENGTH
): PaginationProps => {
  const after = (startCursor - 1 || 0) - pageSize;

  return after <= 0 ? { first: pageSize } : { after: after.toString(), first: pageSize };
};

export interface ConnectionParams {
  /**
   * Cursor for forward pagination. Returns items after this cursor.
   * Used with `first` for paginating forward through the connection.
   */
  after?: string;
  /**
   * Cursor for backward pagination. Returns items before this cursor.
   * Used with `last` for paginating backward through the connection.
   */
  before?: string;
  /**
   * Flag to control whether filters should be applied during pagination requests.
   * When `false`, filters are excluded from subsequent pagination queries to improve performance.
   * @default true
   */
  d_filtersOn: boolean;
  filter: Record<string, any>;
  /**
   * Number of items to fetch in forward pagination.
   * Used with `after` cursor. Cannot be used with `last`.
   */
  first?: number;
  keyword?: string;
  /**
   * Number of items to fetch in backward pagination.
   * Used with `before` cursor. Cannot be used with `first`.
   */
  last?: number;
  searchFilter?: FacetSearchInput;
  sort?: SortInput[];
}

export interface ParseConnectionParamsOptions {
  /**
   * When provided, only keys present in this set are included in the `filter` object.
   * Keys not in the allowlist are silently stripped before the query is sent.
   * Use this to prevent arbitrary URL params from reaching the GraphQL API.
   */
  allowedFilterParams?: Set<string>;
}

export const parseConnectionParams = (
  parameters: Record<string, unknown>,
  listLength = Pagination.LIST_LENGTH,
  options?: ParseConnectionParamsOptions
): ConnectionParams => {
  const { after, before, d_filtersOn, first, keyword, last, searchFilter, sort, ...filter } = parameters as Omit<
    ConnectionParams,
    'filter'
  > &
    Record<string, unknown>;

  for (const parameter of acceptableEmptyParams) {
    if (!filter[parameter]) {
      delete filter[parameter];
    }
  }

  if (options?.allowedFilterParams) {
    for (const key of Object.keys(filter)) {
      if (!options.allowedFilterParams.has(key)) {
        delete filter[key];
      }
    }
  }

  return {
    after,
    before,
    [before ? 'last' : 'first']: listLength,
    d_filtersOn: d_filtersOn ?? true,
    filter,
    keyword,
    searchFilter,
    sort,
  };
};

/**
 * Triggers file download as browser does not prompt by default
 *
 * @param {object} data
 * @param {string} filename
 * @param {string} type
 */
export const downloadAttachment = (data: BlobPart, filename: string, type = '') => {
  const blob = new Blob([data], { type });
  const link = document.createElement('a');

  if (link.download === undefined) {
    return;
  }
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.append(link); // Required for FF
  link.click();
  link.remove();
};

export const parsePageInfo = (): Omit<PageInfo, '__typename'> => ({
  endCursor: String(Pagination.LIST_LENGTH),
  hasNextPage: false,
  hasPreviousPage: false,
  startCursor: '1',
  totalEdges: Pagination.LIST_LENGTH,
});

/**
 * Sets the current API url to localStorage for use everywhere
 */
export const setGraphBaseURL = (url?: string) => {
  if (url === undefined) {
    LoggingService.debug({ message: 'Failed to change graph base, no url specified' });

    return;
  }

  if (process.env.REACT_APP_GRAPH_BASE && !process.env.REACT_APP_GRAPH_BASE.includes(url)) {
    graphBaseUrlStorage.set(url);
    globalThis.location.reload();
  } else {
    clearGraphBaseURL();
  }
};

/**
 * Clears the current API url in localStorage.
 */
export const clearGraphBaseURL = () => {
  graphBaseUrlStorage.remove();
  globalThis.location.reload();
};

/**
 * A util to assert an object `is` a specific TS type when it contains a specific `__typename` value.
 * The `Type` generic is used to assert the type when the specified `__typename` exists.
 *
 * @param object An object to evaluate
 * @param typename The __typename value that will be evaluated
 */
export const isApiType = <Type extends { __typename?: string }>(
  object: unknown,
  typename: Type['__typename']
): object is Type =>
  !!object && typeof object === 'object' && '__typename' in object && object?.__typename === typename;
